import Vue from "vue";
import { debounce } from "lodash";

import { isFaxDeletable, isFaxUnread } from "@/common/faxStatus";
import faxService from "@/services/faxService";
import {
	init as initFaxEvents,
	subscribe as subscribeFaxEvents
} from "@/services/faxEventsService";

const FAXES_PER_PAGE = 20;

const state = {
	error: null,

	// Faxes for the current page only (server-side search + paging).
	faxes: [],
	total: 0,
	faxOnboarding: null,
	faxOnboardingList: [],

	// Last direction fetched, so live Firestore events can refresh the same view.
	direction: "inbound",

	searchQuery: "",

	page: 1,

	selectedFaxIds: [],

	// Inbound faxes received but never viewed/printed/downloaded (live, org-wide).
	unreadCount: 0,

	loading: false
};

const getters = {
	getError: state => state.error,

	getFaxes: state => state.faxes,

	// Server already returns just the current page filtered by the search query.
	getFilteredFaxesTotal: state => state.total,

	getPerPage: () => FAXES_PER_PAGE,

	getPage: state => state.page,

	getFaxesOnPage: state => state.faxes,

	getFaxOnboarding: state => state.faxOnboarding,
	getFaxOnboardingList: state => state.faxOnboardingList,

	getLoading: state => state.loading,

	getSelectedFaxIds: state => state.selectedFaxIds,

	getUnreadCount: state => state.unreadCount,
	hasUnreadFaxes: state => state.unreadCount > 0,

	isFaxSelected: state => faxId => {
		if (!faxId || !Array.isArray(state.selectedFaxIds)) return false;

		return state.selectedFaxIds.includes(faxId);
	}
};

const mutations = {
	clearError(state) {
		state.error = null;
	},

	setError(state, error) {
		state.error = error;
	},

	setFaxes(state, faxes) {
		state.faxes = faxes ?? [];
	},

	setTotal(state, total) {
		state.total = total ?? 0;
	},

	removeFax(state, faxId) {
		const before = state.faxes.length;

		state.faxes = state.faxes.filter(fax => fax.id !== faxId);
		state.selectedFaxIds = state.selectedFaxIds.filter(id => id !== faxId);
		state.total = Math.max(0, state.total - (before - state.faxes.length));
	},

	removeFaxes(state, faxIds) {
		const idSet = new Set(faxIds);
		const before = state.faxes.length;

		state.faxes = state.faxes.filter(fax => !idSet.has(fax.id));
		state.selectedFaxIds = state.selectedFaxIds.filter(id => !idSet.has(id));
		state.total = Math.max(0, state.total - (before - state.faxes.length));
	},

	selectFaxId(state, faxId) {
		if (!faxId) return;
		if (!Array.isArray(state.selectedFaxIds)) {
			state.selectedFaxIds = [];
		}
		if (!state.selectedFaxIds.includes(faxId)) {
			state.selectedFaxIds.push(faxId);
		}
	},

	unselectFaxId(state, faxId) {
		if (!faxId || !Array.isArray(state.selectedFaxIds)) return;
		const index = state.selectedFaxIds.indexOf(faxId);

		if (index !== -1) {
			state.selectedFaxIds.splice(index, 1);
		}
	},

	resetSelectedFaxIds(state) {
		state.selectedFaxIds = [];
	},

	selectAllFilteredFaxes(state) {
		// Selection only feeds bulk delete, so only deletable faxes are selectable.
		state.selectedFaxIds = state.faxes
			.filter(isFaxDeletable)
			.map(fax => fax.id)
			.filter(Boolean);
	},

	setDirection(state, direction) {
		const next = direction ?? "inbound";

		// Reset to the first page only when switching direction, so live
		// refreshes for the same direction keep the current page.
		if (state.direction !== next) {
			state.page = 1;
		}

		state.direction = next;
	},

	setSearchQuery(state, query) {
		state.searchQuery = query ?? "";
		state.page = 1;
	},

	setPage(state, page) {
		state.page = page > 0 ? page : 1;
	},

	setFaxOnboarding(state, onboarding) {
		state.faxOnboarding = onboarding ?? null;
	},

	setFaxOnboardingList(state, onboardingList) {
		state.faxOnboardingList = onboardingList ?? [];
	},

	setLoading(state, value) {
		state.loading = value;
	},

	setUnreadCount(state, count) {
		state.unreadCount = count > 0 ? count : 0;
	},

	markFaxRead(state, { faxId, readAt }) {
		const fax = state.faxes.find(item => item.id === faxId);

		if (fax && !fax.readAt) {
			// readAt is absent on unread records; Vue 2 needs set() for new keys.
			Vue.set(fax, "readAt", readAt);
		}
	}
};

// Coalesces bursts of live Firestore fax events into a single list refresh.
// fetchFaxes reads the current direction at call time, so a tab switch during the debounce wins.
const debouncedRefresh = debounce(dispatch => {
	dispatch("fetchFaxes", { silent: true }).catch(err => {
		console.error("[FAXES] Background refresh failed:", err.message);
	});
}, 800);

// Only the most recent list request may commit, so slower older responses are dropped.
let latestFetchId = 0;

const actions = {
	/**
		* Fetch the current page of organization faxes (server-side search + paging).
		* Reads direction, searchQuery and page from state; pass a direction to switch
		* the tab (which resets to page 1).
		* @param {"inbound" | "outbound"} [direction]
		* @param {boolean} [silent] - skip the loading state (background refresh)
		*/
	async fetchFaxes({ commit, state }, { direction, silent = false } = {}) {
		commit("clearError");

		if (direction !== undefined) {
			commit("setDirection", direction);
		}

		if (!silent) {
			commit("setLoading", true);
		}

		const fetchId = ++latestFetchId;

		try {
			// Already sorted newest-first by the backend.
			const { hits, count } = await faxService.listFaxes({
				direction: state.direction,
				search: state.searchQuery,
				page: state.page,
				limit: FAXES_PER_PAGE
			});

			if (fetchId !== latestFetchId) return state.faxes;

			commit("setFaxes", hits);
			commit("setTotal", count);

			return hits;
		} catch (err) {
			if (fetchId !== latestFetchId) return state.faxes;

			commit("setError", err.message);
			throw err;
		} finally {
			if (fetchId === latestFetchId) {
				commit("setLoading", false);
			}
		}
	},

	/**
		* Apply a search query and reload from the first page.
		* @param {string} query
		*/
	async applySearch({ commit, dispatch }, query) {
		commit("setSearchQuery", query);

		await dispatch("fetchFaxes", {});
	},

	/**
		* Navigate to a page and reload it from the server.
		* @param {number} page
		*/
	async changePage({ commit, dispatch }, page) {
		commit("resetSelectedFaxIds");
		commit("setPage", page);

		await dispatch("fetchFaxes", {});
	},

	/**
		* Fetch a single fax with its signed download URL (not stored in state).
		* Opening, printing and downloading all go through here, so an unread
		* inbound fax is marked as read as a side effect.
		* @param {string} faxId
		* @returns {Promise<FaxView>}
		*/
	async fetchFax({ commit, dispatch }, faxId) {
		commit("clearError");

		try {
			const fax = await faxService.getFax(faxId);

			if (isFaxUnread(fax)) {
				const readAt = await dispatch("markFaxAsRead", fax);

				return readAt ? { ...fax, readAt } : fax;
			}

			return fax;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		}
	},

	/**
		* Mark an unread inbound fax as read. Never throws: a failure here must not
		* block viewing/printing.
		* @param {FaxView} fax
		* @returns {Promise<string | null>} readAt timestamp, or null when skipped/failed
		*/
	async markFaxAsRead({ commit }, fax) {
		if (!isFaxUnread(fax)) return null;

		try {
			const updated = await faxService.markFaxAsRead(fax.id);
			const readAt = updated?.readAt ?? new Date().toISOString();

			commit("markFaxRead", { faxId: fax.id, readAt });

			return readAt;
		} catch (err) {
			console.error("[FAXES] Mark as read failed:", err.message);

			return null;
		}
	},

	/**
		* Refresh the current view in response to a live Firestore fax event.
		* Debounced to coalesce bursts of provider status updates.
		*/
	onFaxEvent({ dispatch }) {
		debouncedRefresh(dispatch);
	},

	/**
		* After a delete empties the current page, move to the last valid page and reload it.
		* Never throws: the delete itself already succeeded.
		*/
	async syncPageAfterDelete({ commit, dispatch, state }) {
		if (state.faxes.length) return;

		const lastPage = Math.max(1, Math.ceil(state.total / FAXES_PER_PAGE));

		commit("setPage", Math.min(state.page, lastPage));

		if (!state.total) return;

		try {
			await dispatch("fetchFaxes", {});
		} catch (err) {
			console.error("[FAXES] Reload after delete failed:", err.message);
		}
	},

	/**
		* Send fax
		* @param {Object} payload
		* @param {string} payload.to
		* @param {boolean} [payload.deleteFileAfterSend]
		* @param {File | Blob} payload.file
		*/
	async sendFax({ commit }, payload) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const response = await faxService.sendFax(
				{
					to: payload.to,
					deleteFileAfterSend: payload.deleteFileAfterSend
				},
				payload.file
			);

			return response;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Delete a fax message (record and stored document)
		* @param {string} faxId
		*/
	async deleteFax({ commit, dispatch }, faxId) {
		commit("clearError");
		commit("setLoading", true);

		try {
			await faxService.deleteFax(faxId);

			commit("removeFax", faxId);
			await dispatch("syncPageAfterDelete");
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Delete multiple fax messages at once
		* @param {string[]} faxIds
		*/
	async deleteFaxes({ commit, dispatch }, faxIds) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const results = await Promise.allSettled(
				faxIds.map(faxId => faxService.deleteFax(faxId))
			);

			// Drop the ones that were deleted even if others failed, so the list matches the server.
			commit("removeFaxes", faxIds.filter((_, i) => results[i].status === "fulfilled"));
			await dispatch("syncPageAfterDelete");

			const failure = results.find(result => result.status === "rejected");

			if (failure) {
				throw failure.reason;
			}
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Create or update fax onboarding
		* @param {Object} payload
		*/
	async upsertFaxOnboarding({ commit }, payload) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const onboarding =
				await faxService.upsertFaxOnboarding(payload);

			commit("setFaxOnboarding", onboarding);

			return onboarding;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Fetch onboarding for current organization
		*/
	async fetchFaxOnboardingByOrganization({ commit }) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const onboarding =
				await faxService.getFaxOnboardingByOrganization();

			commit("setFaxOnboarding", onboarding);

			return onboarding;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Fetch onboarding by phone number
		* @param {string} phoneNumber
		*/
	async fetchFaxOnboardingByPhone(
		{ commit },
		phoneNumber
	) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const onboarding =
				await faxService.getFaxOnboardingByPhone(phoneNumber);

			commit("setFaxOnboarding", onboarding);

			return onboarding;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	},

	/**
		* Fetch fax onboarding list
		* @param {boolean} [faxEnabled]
		*/
	async fetchFaxOnboardingList(
		{ commit },
		{ faxEnabled } = {}
	) {
		commit("clearError");
		commit("setLoading", true);

		try {
			const onboardingList =
				await faxService.listFaxOnboarding(faxEnabled);

			commit("setFaxOnboardingList", onboardingList);

			return onboardingList;
		} catch (err) {
			commit("setError", err.message);
			throw err;
		} finally {
			commit("setLoading", false);
		}
	}
};

export default {
	namespaced: true,
	state,
	getters,
	mutations,
	actions,

	init({ watch, login, dispatch, commit }, rootStore) {
		initFaxEvents({
			dispatch,
			setUnreadCount: count => commit("setUnreadCount", count),
			notify: payload =>
				rootStore.commit("alerts/add", { timeout: true, ...payload })
		});

		watch(
			() => login.userGroup?.id,
			(userGroupId, previousUserGroupId) => {
				if (userGroupId && userGroupId !== previousUserGroupId && login.profile?.clinic) {
					subscribeFaxEvents(login.profile.clinic);
				}
			}
		);

		watch(
			() => login.refreshed,
			refreshed => {
				if (refreshed && login.profile?.clinic) {
					subscribeFaxEvents(login.profile.clinic);
				}
			}
		);
	}
};
